import { motion } from 'motion/react'
import { ComponentPropsWithoutRef, ElementType, ReactNode, useMemo } from 'react'

import { cn } from '@sparkai/utils'

import { Tooltip } from '../../General/Tooltip'
import { Icon, IconsName } from '../../Icon'
import { getMotionPolicy, getPolicyTransition, instantTransition } from '../../Wrapper'
import { useSideBar } from '../SideBar/SidebarContext'

export interface MenuLinkBaseProps {
  active?: boolean
  label?: string
  description?: string
  icon: IconsName
  iconDot?: boolean
  collapsed?: boolean
  variant: 'primary' | 'secondary'
  descriptionProps?: ComponentPropsWithoutRef<'p'>
  collapsedIconProps?: React.SVGProps<SVGSVGElement>
  expandedIconProps?: React.SVGProps<SVGSVGElement>
  labelProps?: ComponentPropsWithoutRef<'p'>
  rightAddon?: ReactNode
  dotIconAddon?: ReactNode
  countBadge?: number
  children?: ReactNode
  isMobile?: boolean
  isTooltipDisabled?: boolean
  disabled?: boolean
}

export type MenuLinkProps<P extends ElementType> = {
  as?: P
} & ComponentPropsWithoutRef<P> &
  MenuLinkBaseProps

function Badge({ count }: Readonly<{ count: number }>) {
  return (
    <span className='rounded-full bg-blue-500 px-[6px] py-[2px] text-xs leading-4 font-medium text-white'>
      {count}
    </span>
  )
}

function CollapsedMenuLink<P extends ElementType>({
  Component,
  active,
  disabled,
  icon,
  collapsedIconProps,
  dotIconAddon,
  label,
  isMobile,
  isTooltipDisabled,
  variant,
  props,
}: Readonly<{
  Component: ElementType
  active?: boolean
  disabled: boolean
  icon: IconsName
  collapsedIconProps?: React.SVGProps<SVGSVGElement>
  dotIconAddon?: ReactNode
  label?: string
  isMobile?: boolean
  isTooltipDisabled: boolean
  variant: 'primary' | 'secondary'
  props: ComponentPropsWithoutRef<P>
}>) {
  if (variant !== 'primary') return null

  return (
    <Tooltip disabled={isMobile || isTooltipDisabled} content={label} placement='right'>
      <Component
        {...props}
        className={cn(
          'relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-md p-[10px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
          active ? 'text-blue-500' : 'text-gray-400',
          active ? 'border-l-blue-500' : 'border-l-transparent',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <Icon {...collapsedIconProps} className={cn('h-6 w-6', collapsedIconProps?.className)} name={icon} />
        {dotIconAddon}
      </Component>
    </Tooltip>
  )
}

function ExpandedMenuLink<P extends ElementType>({
  Component,
  active,
  disabled,
  icon,
  expandedIconProps,
  dotIconAddon,
  label,
  labelProps,
  description,
  descriptionProps,
  rightAddon,
  countBadge,
  children,
  variant,
  props,
}: Readonly<{
  Component: ElementType
  active?: boolean
  disabled: boolean
  icon: IconsName
  expandedIconProps?: React.SVGProps<SVGSVGElement>
  dotIconAddon?: ReactNode
  label?: string
  labelProps?: ComponentPropsWithoutRef<'p'>
  description?: string
  descriptionProps?: ComponentPropsWithoutRef<'p'>
  rightAddon?: ReactNode
  countBadge?: number
  children?: ReactNode
  variant: 'primary' | 'secondary'
  props: ComponentPropsWithoutRef<P>
}>) {
  return (
    <Component {...props}>
      <div
        className={cn(
          'cursor-pointer border-l-2 px-5',
          variant === 'primary' ? 'w-72' : 'w-64',
          active ? 'text-blue-500' : 'text-gray-400',
          active ? 'border-l-blue-500' : 'border-l-transparent',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <div className='flex items-center gap-[15px] px-2 py-2.5'>
          <div className='relative flex h-6 w-6 items-center justify-center'>
            <Icon {...expandedIconProps} name={icon} />
            {dotIconAddon}
          </div>

          <p
            {...labelProps}
            className={cn(
              'flex flex-1 justify-between font-inter text-sm font-medium',
              active ? 'text-blue-500' : 'text-gray-600',
              labelProps?.className
            )}
          >
            {label} {Boolean(countBadge) && <Badge count={countBadge ?? 0} />}
          </p>

          {rightAddon}
        </div>

        {description && (
          <p
            {...descriptionProps}
            className={cn(
              'px-2 pb-3 font-inter text-xs font-normal text-gray-500',
              descriptionProps?.className
            )}
          >
            {description}
          </p>
        )}

        <div className='px-2'>{children}</div>
      </div>
    </Component>
  )
}

function PrimaryMenuLink<P extends ElementType>({
  Component,
  active,
  disabled,
  icon,
  collapsedIconProps,
  expandedIconProps,
  dotIconAddon,
  label,
  labelProps,
  rightAddon,
  countBadge,
  isCollapsed,
  isMobile,
  isTooltipDisabled,
  props,
}: Readonly<{
  Component: ElementType
  active?: boolean
  disabled: boolean
  icon: IconsName
  collapsedIconProps?: React.SVGProps<SVGSVGElement>
  expandedIconProps?: React.SVGProps<SVGSVGElement>
  dotIconAddon?: ReactNode
  label?: string
  labelProps?: ComponentPropsWithoutRef<'p'>
  rightAddon?: ReactNode
  countBadge?: number
  isCollapsed: boolean
  isMobile?: boolean
  isTooltipDisabled: boolean
  props: ComponentPropsWithoutRef<P>
}>) {
  const isMobilePrimaryLink = Boolean(isMobile)
  const { layoutTransition, contentTransition } = useMemo(() => {
    const motionPolicy = getMotionPolicy()
    return {
      layoutTransition: motionPolicy.instant
        ? instantTransition
        : getPolicyTransition(motionPolicy, { duration: 0.2, ease: 'easeInOut', type: 'tween' }),
      contentTransition: motionPolicy.instant
        ? instantTransition
        : getPolicyTransition(motionPolicy, { duration: 0.12, ease: 'easeOut' }),
    }
  }, [])

  return (
    <Tooltip disabled={!isCollapsed || isMobile || isTooltipDisabled} content={label} placement='right'>
      <div className={cn('overflow-hidden', isMobilePrimaryLink ? 'w-11' : 'w-full')}>
        <Component
          {...props}
          aria-label={label}
          className={cn(
            'relative flex h-11 cursor-pointer items-center rounded-md border-l-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
            isMobilePrimaryLink ? 'w-11 justify-center p-[10px]' : 'w-full pl-[20px]',
            active ? 'text-blue-500' : 'text-gray-400',
            'border-l-transparent',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {active && !isCollapsed && (
            <motion.span
              layoutId='primary-sidebar-active-rail'
              transition={layoutTransition}
              className='absolute top-0 bottom-0 left-0 w-0.5 bg-blue-500'
            />
          )}
          <div className='relative flex h-6 w-6 shrink-0 items-center justify-center'>
            {isCollapsed ? (
              <Icon
                {...collapsedIconProps}
                className={cn('size-5', collapsedIconProps?.className)}
                name={icon}
              />
            ) : (
              <Icon
                {...expandedIconProps}
                className={cn('size-5', expandedIconProps?.className)}
                name={icon}
              />
            )}
            {dotIconAddon}
          </div>

          {!isMobilePrimaryLink && (
            <motion.div
              initial={false}
              animate={{ opacity: isCollapsed ? 0 : 1 }}
              transition={contentTransition}
              aria-hidden={isCollapsed}
              className={cn(
                'ml-[15px] flex w-[209px] shrink-0 items-center justify-between whitespace-nowrap',
                isCollapsed && 'pointer-events-none'
              )}
            >
              <p
                {...labelProps}
                className={cn(
                  'flex flex-1 justify-between font-inter text-sm font-medium whitespace-nowrap',
                  active ? 'text-blue-500' : 'text-gray-600',
                  labelProps?.className
                )}
              >
                {label} {Boolean(countBadge) && <Badge count={countBadge ?? 0} />}
              </p>

              {rightAddon}
            </motion.div>
          )}
        </Component>
      </div>
    </Tooltip>
  )
}

export function MenuLink<P extends ElementType = 'a'>({
  as,
  className,
  collapsed,
  variant,
  active,
  icon,
  label,
  description,
  descriptionProps,
  collapsedIconProps,
  expandedIconProps,
  labelProps,
  rightAddon,
  dotIconAddon,
  countBadge,
  children,
  isMobile,
  isTooltipDisabled = false,
  disabled = false,
  ...props
}: MenuLinkProps<P>) {
  const Component = as ?? 'a'
  const cssOverrides = className
  const { isCollapsed: isSideBarCollapsed } = useSideBar()
  const isCollapsed = collapsed ?? isSideBarCollapsed
  const isPrimary = variant === 'primary'

  let content: ReactNode

  if (isPrimary) {
    content = (
      <PrimaryMenuLink
        Component={Component}
        active={active}
        disabled={disabled}
        icon={icon}
        collapsedIconProps={collapsedIconProps}
        expandedIconProps={expandedIconProps}
        dotIconAddon={dotIconAddon}
        label={label}
        labelProps={labelProps}
        rightAddon={rightAddon}
        countBadge={countBadge}
        isCollapsed={Boolean(isCollapsed)}
        isMobile={isMobile}
        isTooltipDisabled={isTooltipDisabled}
        props={props}
      />
    )
  } else if (isCollapsed) {
    content = (
      <CollapsedMenuLink
        Component={Component}
        active={active}
        disabled={disabled}
        icon={icon}
        collapsedIconProps={collapsedIconProps}
        dotIconAddon={dotIconAddon}
        label={label}
        isMobile={isMobile}
        isTooltipDisabled={isTooltipDisabled}
        variant={variant}
        props={props}
      />
    )
  } else {
    content = (
      <ExpandedMenuLink
        Component={Component}
        active={active}
        disabled={disabled}
        icon={icon}
        expandedIconProps={expandedIconProps}
        dotIconAddon={dotIconAddon}
        label={label}
        labelProps={labelProps}
        description={description}
        descriptionProps={descriptionProps}
        rightAddon={rightAddon}
        countBadge={countBadge}
        variant={variant}
        props={props}
      >
        {children}
      </ExpandedMenuLink>
    )
  }

  return (
    <div
      className={cn(
        'MenuLink flex items-center justify-center',
        variant === 'primary' ? 'pb-2' : '',
        cssOverrides
      )}
    >
      {content}
    </div>
  )
}
